import { useRef, useState } from 'react';
import { Trophy, Camera, Loader2 } from 'lucide-react';

interface RankingGroupImageProps {
  imageUrl?: string | null;
  size?: 'sm' | 'md';
  editable?: boolean;
  onImageSelected?: (base64: string) => Promise<void>;
}

const SIZE_CLASSES = {
  sm: 'w-12 h-12 rounded-xl',
  md: 'w-16 h-16 rounded-xl',
};

const ICON_SIZES = {
  sm: 'w-6 h-6',
  md: 'w-8 h-8',
};

export function RankingGroupImage({
  imageUrl,
  size = 'sm',
  editable = false,
  onImageSelected,
}: RankingGroupImageProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !onImageSelected) return;

    // Reset input so same file can be re-selected
    e.target.value = '';

    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result as string;
      setIsUploading(true);
      try {
        await onImageSelected(base64);
      } finally {
        setIsUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const sizeClass = SIZE_CLASSES[size];
  const iconClass = ICON_SIZES[size];

  const imageContent = imageUrl ? (
    <img
      src={imageUrl}
      alt="Ranking"
      className={`${sizeClass} object-cover shrink-0`}
    />
  ) : (
    <div className={`${sizeClass} bg-white/10 flex items-center justify-center shrink-0`}>
      <Trophy className={`${iconClass} text-turquoise`} />
    </div>
  );

  if (!editable) {
    return imageContent;
  }

  return (
    <button
      type="button"
      onClick={() => fileInputRef.current?.click()}
      disabled={isUploading}
      className={`${sizeClass} relative group shrink-0 overflow-hidden`}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt="Ranking"
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full bg-white/10 flex items-center justify-center">
          <Trophy className={`${iconClass} text-turquoise`} />
        </div>
      )}

      {/* Overlay: always visible when uploading or no image; hover-only on desktop when image exists */}
      {(isUploading || !imageUrl) && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
          {isUploading ? (
            <Loader2 className="w-5 h-5 text-white animate-spin" />
          ) : (
            <Camera className="w-5 h-5 text-white" />
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />
    </button>
  );
}
